import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Facebook, Twitter, Instagram, Linkedin, Mail, Phone, MapPin, Send, Heart } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    { 
      title: 'Compañía', 
      items: [
        { name: 'Sobre Nosotros', path: '/about' },
        { name: 'Servicios', path: '/services' },
        { name: 'Contacto', path: '/contact' }
      ] 
    },
    { 
      title: 'Servicios', 
      items: [
        { name: 'Construcción', path: '/services/construccion' },
        { name: 'Renovación', path: '/services/renovacion' },
        { name: 'Diseño', path: '/services/diseno' }
      ] 
    },
    { 
      title: 'Recursos', 
      items: [
        { name: 'Herramientas', path: '/tools' },
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'FAQ', path: '/about' }
      ] 
    },
  ];

  const socialLinks = [
    { icon: Facebook, url: 'https://facebook.com', color: 'hover:text-blue-500' },
    { icon: Twitter, url: 'https://twitter.com', color: 'hover:text-sky-400' },
    { icon: Instagram, url: 'https://instagram.com', color: 'hover:text-pink-500' },
    { icon: Linkedin, url: 'https://linkedin.com', color: 'hover:text-blue-600' },
  ];

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-600 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-600 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 mb-12">
          <div className="lg:col-span-4">
            <Link to="/" className="inline-flex items-center gap-3 group">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <span className="text-2xl font-display font-bold bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
                ConstructConnect
              </span>
            </Link>
            <p className="mt-6 text-gray-400 leading-relaxed">
              Conectando profesionales de la construcción con clientes visionarios. Transformamos sueños en realidad con calidad y profesionalismo.
            </p>

            <div className="mt-8">
              <h4 className="font-semibold mb-3 text-white">Newsletter</h4>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Tu email"
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:border-primary-500 text-white placeholder-gray-400"
                />
                <button className="px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:shadow-glow transition-all">
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {footerLinks.map((section, index) => (
            <div key={index} className="lg:col-span-2">
              <h3 className="text-lg font-bold mb-6 text-white">{section.title}</h3>
              <ul className="space-y-3">
                {section.items.map((item, itemIndex) => (
                  <li key={itemIndex}>
                    <Link 
                      to={item.path} 
                      className="text-gray-400 hover:text-primary-400 transition-colors duration-200 flex items-center gap-2 group"
                    >
                      <span className="w-0 group-hover:w-2 h-0.5 bg-primary-400 transition-all duration-200" />
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-4">
            <h3 className="text-lg font-bold mb-6 text-white">Contacto</h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href="mailto:info@constructconnect.com" 
                  className="flex items-start gap-3 text-gray-400 hover:text-primary-400 transition-colors group"
                >
                  <div className="p-2 bg-white/10 rounded-lg group-hover:bg-primary-600/20 transition-colors">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Email</div>
                    <div className="font-medium">info@constructconnect.com</div>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href="tel:+1234567890" 
                  className="flex items-start gap-3 text-gray-400 hover:text-primary-400 transition-colors group"
                >
                  <div className="p-2 bg-white/10 rounded-lg group-hover:bg-primary-600/20 transition-colors">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Teléfono</div>
                    <div className="font-medium">+1 (234) 567-890</div>
                  </div>
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <div className="p-2 bg-white/10 rounded-lg">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Ubicación</div>
                  <div className="font-medium">Building City, 12345</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  className={`p-3 bg-white/10 rounded-lg ${social.color} transition-colors duration-200`}
                >
                  <social.icon size={20} />
                </motion.a>
              ))}
            </div>

            <div className="text-center md:text-right">
              <p className="text-gray-400 text-sm flex items-center gap-2 justify-center md:justify-end">
                © {currentYear} ConstructConnect. Hecho con
                <Heart className="w-4 h-4 text-red-500 animate-pulse" fill="currentColor" />
                en Colombia
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;